import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-401" data-mp="2xo5120q7niyt" />;
  const customer = customerById(item.customerId);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-402" data-mp="2sp25d6w4ep3i">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-403" data-mp="37garv74q7ucf">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-404" data-mp="3ggfwxamg6gnf" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-405" data-mp="3cf430swjj99f">
        <div data-id="element-406" data-mp="3cdawddlxgp1f">
          <p className="text-sm text-ink-muted" data-id="element-407" data-mp="3jibzbfkg4vdc">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-408" data-mp="3jk57izapjnng">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-409" data-mp="3tfgo7n5of4xu">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-410" data-mp="23nho31xgzzu">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-411" data-mp="b4bu4tboasam">
          <StatusBadge status={item.status} data-id="element-412" data-mp="g2n557dpf8uf" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-413" data-mp="fzr77j1uwk5z">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-414" data-mp="z441zmbxblrn" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-415" data-mp="q6lembat4vcl">
          <p className="text-[15px] text-ink" data-id="element-416" data-mp="1mtoy76x38fiv">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-417" data-mp="1iviwidb3wq4t">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-418" data-mp="1duboqe8yrqy5">
            Åbn tilbud
          </Link>
        </section>}

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-419" data-mp="18ye9ycdcmsnh">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-420" data-mp="2dv4m7mgsm4y">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-421" data-mp="3tko333pvi75s" />
        </div>
        <div className="mt-6" data-id="element-422" data-mp="1hbz5q5vqnmlk">
          <StatusTimeline status={item.status} data-id="element-423" data-mp="1hd0s36i7fvil" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-424" data-mp="2510gw0vyhbuj">
        <div className="space-y-5" data-id="element-425" data-mp="24zpd412ig43f">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-426" data-mp="293n9w54d52tb">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-427" data-mp="1v3ckl6ipbwa9">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-428" data-mp="202gtrx5mlu0l">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-429" data-mp="jnflwql29xzu">
              <div data-id="element-430" data-mp="oobkqpqk2tye">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-431" data-mp="oov4mbzrnsf2">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-432" data-mp="h27s38arvghi">{item.category}</dd>
              </div>
              <div data-id="element-433" data-mp="c3wld6frxm3i">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-434" data-mp="c6shcp82a0pb">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-435" data-mp="q3o2hg1l7ilq">{item.address || '—'}</dd>
              </div>
              <div data-id="element-436" data-mp="l65vhv74rdgd">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-437" data-mp="3tl5ymq7zz16b">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-438" data-mp="3titmwusu1n1p">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-439" data-mp="764orz46pbsz" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-440" data-mp="35uttfn44zijf">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-441" data-mp="19t8km4q5npec">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-442" data-mp="1er09sqzf0xeu">
              <label htmlFor="ny-note" className="sr-only" data-id="element-443" data-mp="que5gums4mxn">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-444" data-mp="vrw7e12dq1w5" />
              <Button type="submit" variant="dark" data-id="element-445" data-mp="2gadl8ukwlam9">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-446" data-mp="ju7wrzxmpke">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-447" data-mp="3rxzxytp37cxk">
                  <div data-id="element-448" data-mp="3czxt8g6bkleq">
                    <p className="text-xs text-ink-muted" data-id="element-449" data-mp="3czfqd0p5j8xu">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-450" data-mp="38zxf6wn4uc87">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-451" data-mp="3425fhgg8xltg">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-452" data-mp="g2ewv0p8q7vw" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-453" data-mp="6clqqf7vakz1">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-454" data-mp="3ohf52a4yslsp">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-455" data-mp="3sklaxgo60ayn">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-456" data-mp="3ekaii977iobd">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-457" data-mp="3jjdopwyxb0kx">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-458" data-mp="3jjx71eshjqzh">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-459" data-mp="1hrs7bh4qvpe7">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-460" data-mp="3g17v6q8gsxx3">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-461" data-mp="20mk42ghb065w" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-462" data-mp="2lzcmnsfmhsil" />}
                  {uploading ? 'Uploader …' : 'Upload'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={async event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  input.value = '';
                  setUploadError(problems.join(' '));
                  if (accepted.length === 0) return;
                  setUploading(true);
                  const result = await addPhotos(item.id, accepted, stage);
                  setUploading(false);
                  if (!result.ok) setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                }} data-id="element-463" data-mp="2gzgh3ora1t0x" />
                </label>
              </div>
            </div>

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-464" data-mp="3si59fipebbn">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-465" data-mp="8q9xybr6a1gw">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-466" data-mp="hqyebd41gdwy">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-467" data-mp="mjrgpuc1ba5v">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-468" data-mp="rl8kbxlwlaq5">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-469" data-mp="11gsa029vjd6g">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-470" data-mp="11exnwkmv9f0s" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-471" data-mp="ljrb08x27ppm">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-472" data-mp="zj0h2frjx3fv" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-473" data-mp="31b5eg6rtksxz">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-474" data-mp="1lpuqfu1myl8m">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-475" data-mp="1lsz2fdlcel9u">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-476" data-mp="171zvs842i6sw">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-477" data-mp="1bzi8m9l7h666">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-478" data-mp="123op2692kcew">
              <li data-id="element-479" data-mp="o3dvgvgdkjq1">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-480" data-mp="o4p9rp7g59ve">{customer?.email || '—'}</li>
              <li data-id="element-481" data-mp="t1yvatrpt1dy">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-482" data-mp="t4l4exu97oya">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-483" data-mp="2iiq725nclotd">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-484" data-mp="2nf6wd36aa981" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-485" data-mp="ndqlbuc2frwo">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-486" data-mp="ifp3tduag0nb" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-487" data-mp="9fabvvmtjnau">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-488" data-mp="3qupi950lvask">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-489" data-mp="3qsmj9vhh67bp">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-490" data-mp="3mrkivbbtnp85">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-491" data-mp="3htrf0oxzr7b1">
                <dt className="text-ink-soft" data-id="element-492" data-mp="3cuxz6pv8f47p">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-493" data-mp="3cxk5ynaatfov">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-494" data-mp="1xc02hgwp4j82">
                <dt className="text-ink-soft" data-id="element-495" data-mp="1xectejkfm3x9">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-496" data-mp="22cecv5lrs599">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-497" data-mp="26bn52615vv2p">
                <dt className="text-ink-soft" data-id="element-498" data-mp="19n7dy5ieldnh">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-499" data-mp="19kkwne5pnjs2">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-500" data-mp="1indk16brvbcm" />
          </section>
        </aside>
      </div>
    </div>;
}